import React from 'react';
import { motion } from 'motion/react';
import { X, Star, Heart, Star as StarIcon, Sparkles, Crown, Gift, Zap } from 'lucide-react';
import { UserData } from '../hooks/useUser';
import { cn } from '../lib/utils';
import { audioEngine } from '../utils/audioEngine';
import { useTranslation } from 'react-i18next';

interface PremiumModalProps {
  user: UserData;
  onClose: () => void;
  onSetPremiumStatus: (status: boolean, forceDirect?: boolean) => Promise<void>;
}

export default function PremiumModal({ user, onClose, onSetPremiumStatus }: PremiumModalProps) {
  const { t } = useTranslation();
  const isPremium = user.isPremium;
  const [tapCount, setTapCount] = React.useState(0);

  const handleCrownTap = async () => {
    const nextCount = tapCount + 1;
    if (nextCount >= 5) {
      setTapCount(0);
      audioEngine.playSound('success');
      await onSetPremiumStatus(!isPremium, true);
    } else {
      setTapCount(nextCount);
      // Reset taps if inactive for 2 seconds
      setTimeout(() => setTapCount(0), 2000);
    }
  };

  const handleToggle = async () => {
    audioEngine.playSound('click');
    await onSetPremiumStatus(!isPremium);
  };

  return (
    <div className="absolute inset-0 z-[10000] flex items-center justify-center p-6">
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/80 backdrop-blur-md"
      />
      
      <motion.div 
        initial={{ scale: 0.9, opacity: 0, y: 50 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 50 }}
        className="game-card-3d border-none bg-white w-full max-w-sm relative z-10 overflow-hidden shadow-2xl"
      >
        {/* Header with Background */}
        <div className="relative h-40 bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(white 2px, transparent 2px)', backgroundSize: '20px 20px' }} />
          <motion.div 
            animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 1] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            onClick={handleCrownTap}
            className="cursor-pointer"
          >
            <Crown className="w-24 h-24 text-white drop-shadow-2xl" />
          </motion.div>
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/20 to-transparent">
            <h2 className="text-black font-num text-4xl text-center uppercase tracking-tight">{t('premium.title')}</h2>
          </div>
          <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/40 rounded-full text-white transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-8">
          <div className="space-y-6 mb-8">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-rose-100 rounded-2xl flex items-center justify-center shrink-0">
                <Zap className="w-6 h-6 text-rose-600 fill-current" />
              </div>
              <div>
                <h4 className="font-num text-lg text-slate-900 leading-tight uppercase">{t('premium.benefit_1_title')}</h4>
                <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest leading-relaxed">{t('premium.benefit_1_desc')}</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-purple-100 rounded-2xl flex items-center justify-center shrink-0">
                <Gift className="w-6 h-6 text-purple-600 fill-current" />
              </div>
              <div>
                <h4 className="font-num text-lg text-slate-900 leading-tight uppercase">{t('premium.benefit_2_title')}</h4>
                <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest leading-relaxed">{t('premium.benefit_2_desc')}</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-sky-100 rounded-2xl flex items-center justify-center shrink-0">
                <Sparkles className="w-6 h-6 text-sky-600 fill-current" />
              </div>
              <div>
                <h4 className="font-num text-lg text-slate-900 leading-tight uppercase">{t('premium.benefit_3_title')}</h4>
                <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest leading-relaxed">{t('premium.benefit_3_desc')}</p>
              </div>
            </div>
          </div>

          <button
            onClick={async () => {
              audioEngine.playSound('click');
              if (isPremium) {
                const expiryDate = user.subscription_expiry 
                  ? new Date(user.subscription_expiry) 
                  : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                const formattedDate = expiryDate.toLocaleDateString(undefined, { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                });
                const confirmed = window.confirm(t('premium.cancel_confirm', { date: formattedDate }));
                if (!confirmed) return;
              }
              await onSetPremiumStatus(!isPremium);
            }}
            className={cn(
              "toy-3d-button border-none w-full py-4 font-num text-2xl uppercase tracking-tight transition-all flex flex-col items-center justify-center gap-0",
              isPremium 
                ? "bg-rose-600 text-white" 
                : "bg-amber-400 text-slate-900"
            )}
          >
            {isPremium ? (
              <span>{t('premium.cancel_sub')}</span>
            ) : (
              <>
                <span>{t('premium.join_for')}</span>
                <span className="text-[11px] opacity-80 font-black tracking-widest uppercase">{t('premium.for_30_days')}</span>
              </>
            )}
          </button>
          
          <div className="mt-4 space-y-2 text-center">
            <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest italic">
              {isPremium 
                ? t('premium.status_enjoying')
                : t('premium.status_renews')}
            </p>
            <p 
              className="text-[7px] font-bold text-slate-300 uppercase tracking-tighter leading-none px-4"
              dangerouslySetInnerHTML={{ __html: t('premium.agreement') }}
            />
          </div>
        </div>
      </motion.div>
    </div>
  );
}
